import {
  listCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  getUploadUrl,
  getCourseVisibility,
  setCourseVisibility,
  CourseCatalogItem,
} from '@training24/course/src/superAdmin'
import {
  getAllOrganisations,
  removeOrganisationAsSuperAdmin,
  store as storeOrganisation,
} from '@training24/organisation/src/organisation'
import { store as storePermission } from '@training24/organisation/src/permission'
import { IPermissionTypes } from '@training24/organisation/types/permission'
import { isSuperAdmin as isSuperAdminApi } from '@training24/organisation/src/superAdmin'

export type { CourseCatalogItem }

export interface OrgListItem {
  organisationId: string
  name: string
  platformId?: string
}

interface SuperAdminState {
  courses: CourseCatalogItem[]
  coursesLoaded: boolean
  allOrganisations: OrgListItem[]
  organisationsLoaded: boolean
  isSuperAdminFlag: boolean
  superAdminLoaded: boolean
}

export default {
  namespaced: true,

  state: (): SuperAdminState => ({
    courses: [],
    coursesLoaded: false,
    allOrganisations: [],
    organisationsLoaded: false,
    isSuperAdminFlag: false,
    superAdminLoaded: false,
  }),

  getters: {
    // Zentral validiert über den organisation-Service (siehe LOAD_SUPER_ADMIN_STATUS).
    isSuperAdmin: (state: SuperAdminState): boolean => state.isSuperAdminFlag,
    getCourses: (state: SuperAdminState) => state.courses,
    getAllOrganisations: (state: SuperAdminState) => state.allOrganisations,
  },

  actions: {
    /**
     * Super-Admin-Status zentral über den organisation-Service ermitteln und cachen.
     * Einzige Quelle der Wahrheit ist die Allowlist im organisation-Service.
     */
    async LOAD_SUPER_ADMIN_STATUS(context: any, opts?: { force?: boolean }) {
      if (context.state.superAdminLoaded && !(opts && opts.force)) return
      const jwt = context.rootGetters['getJWT']
      if (!jwt) return
      try {
        const ok = await isSuperAdminApi(jwt)
        context.commit('SET_SUPER_ADMIN', ok)
      } catch {
        context.commit('SET_SUPER_ADMIN', false)
      }
    },

    async LOAD_COURSES(context: any) {
      const jwt = context.rootGetters['getJWT']
      const courses = await listCourses(jwt)
      context.commit('SET_COURSES', courses)
    },

    async CREATE_COURSE(context: any, payload: Partial<CourseCatalogItem>) {
      const jwt = context.rootGetters['getJWT']
      const course = await createCourse(payload, jwt)
      context.commit('ADD_COURSE', course)
      return course
    },

    async UPDATE_COURSE(context: any, payload: CourseCatalogItem) {
      const jwt = context.rootGetters['getJWT']
      const updated = await updateCourse(payload.courseId, payload, jwt)
      context.commit('UPDATE_COURSE', updated)
      return updated
    },

    async DELETE_COURSE(context: any, courseId: string) {
      const jwt = context.rootGetters['getJWT']
      await deleteCourse(courseId, jwt)
      context.commit('REMOVE_COURSE', courseId)
    },

    async GET_UPLOAD_URL(_context: any, { key, contentType, jwt }: { key: string; contentType: string; jwt: string }) {
      return getUploadUrl(key, contentType, jwt)
    },

    async GET_VISIBILITY(_context: any, { courseId, jwt }: { courseId: string; jwt: string }) {
      return getCourseVisibility(courseId, jwt)
    },

    async SET_VISIBILITY(_context: any, { courseId, orgIds, jwt }: { courseId: string; orgIds: string[]; jwt: string }) {
      return setCourseVisibility(courseId, orgIds, jwt)
    },

    async LOAD_ALL_ORGANISATIONS(context: any, opts?: { force?: boolean }) {
      if (context.state.organisationsLoaded && !(opts && opts.force)) return
      const jwt = context.rootGetters['getJWT']
      const orgs = await getAllOrganisations(jwt)
      context.commit('SET_ALL_ORGANISATIONS', orgs)
    },

    /**
     * Löscht eine Organisation samt Berechtigungen und Gruppen.
     * Kurse, Unterweisungen und Nachweise der Einrichtung bleiben in den
     * jeweiligen Diensten bestehen, sind ohne Organisation aber nicht mehr
     * erreichbar.
     */
    async DELETE_ORGANISATION(context: any, organisationId: string) {
      const jwt = context.rootGetters['getJWT']
      await removeOrganisationAsSuperAdmin(organisationId, jwt)
      context.commit('REMOVE_ORGANISATION', organisationId)
    },

    /**
     * Legt eine neue Organisation an und macht den angegebenen Admin-User
     * zum OWNER (inkl. Cognito-Anlage, ohne Einladungs-E-Mail – Login per OTP).
     * Komponiert die bestehenden SDK-Endpunkte; kein neuer Backend-Endpoint nötig.
     */
    async CREATE_ORG_WITH_ADMIN(
      context: any,
      { orgName, adminName, adminEmail }: { orgName: string; adminName: string; adminEmail: string },
    ) {
      const jwt = context.rootGetters['getJWT']

      // 1) Organisation anlegen
      const org = await storeOrganisation(
        { platformId: 'meda', name: orgName, data: { company: { name: orgName } } },
        jwt,
      )

      // 2) Admin-User als OWNER anlegen (Cognito-Konto, keine Einladungs-Mail)
      try {
        await storePermission(
          org.organisationId,
          {
            email: adminEmail.trim(),
            userName: adminName.trim().toLowerCase().replace(' ', '-'),
            firstName: adminName.trim().split(' ')[0] || '',
            lastName: adminName.trim().split(' ').slice(1).join(' ') || '',
            type: IPermissionTypes.OWNER,
            sendInivtiationEmail: false,
            consentGiven: false,
          } as any,
          jwt,
        )
      } catch (e: any) {
        // 409 = bereits Mitglied. Bei einer frisch angelegten Org kann das nur der
        // Ersteller selbst sein (wird beim Anlegen automatisch OWNER) – Zielzustand
        // ist damit erreicht, also kein Fehler.
        if (e?.response?.status !== 409) throw e
      }

      // 3) Liste lokal aktualisieren
      context.commit('ADD_ORGANISATION', {
        organisationId: org.organisationId,
        name: orgName,
        platformId: 'meda',
      })

      return org
    },
  },

  mutations: {
    SET_SUPER_ADMIN(state: SuperAdminState, v: boolean) {
      state.isSuperAdminFlag = v
      state.superAdminLoaded = true
    },
    SET_COURSES(state: SuperAdminState, courses: CourseCatalogItem[]) {
      state.courses = courses
      state.coursesLoaded = true
    },
    ADD_COURSE(state: SuperAdminState, course: CourseCatalogItem) {
      state.courses = [...state.courses, course]
    },
    UPDATE_COURSE(state: SuperAdminState, updated: CourseCatalogItem) {
      state.courses = state.courses.map((c) => (c.courseId === updated.courseId ? updated : c))
    },
    REMOVE_COURSE(state: SuperAdminState, courseId: string) {
      state.courses = state.courses.filter((c) => c.courseId !== courseId)
    },
    SET_ALL_ORGANISATIONS(state: SuperAdminState, orgs: OrgListItem[]) {
      state.allOrganisations = orgs
      state.organisationsLoaded = true
    },
    ADD_ORGANISATION(state: SuperAdminState, org: OrgListItem) {
      state.allOrganisations = [...state.allOrganisations, org]
    },
    REMOVE_ORGANISATION(state: SuperAdminState, organisationId: string) {
      state.allOrganisations = state.allOrganisations.filter((o) => o.organisationId !== organisationId)
    },
  },
}
